import type { ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import { styled } from '@components-shared/src/stitches';
import { useStableCallback } from '@hooks/useStableCallback';
import { animationStyles } from '../shared';
import { useGetLayoutContainer } from '../useGetLayoutContainer';
import { TooltipContextProvider, useTooltipContext } from './Context';

type TooltipRootProps = Omit<React.ComponentProps<typeof Root>, 'open'>;

function TooltipComponent({
  children,
  delayDuration = 0,
  ...props
}: {
  children: ReactNode;
} & TooltipRootProps) {
  const { openContent } = useTooltipContext();

  return (
    <Provider delayDuration={delayDuration}>
      <Root delayDuration={delayDuration} open={openContent} {...props}>
        {children}
      </Root>
    </Provider>
  );
}

export function Tooltip({
  children,
  openOnClick,
  delayCloseMs,
  ...props
}: {
  children: React.ReactNode;
  /**
   * @note we default open on hover - setting this to true will open on click which is useful for mobile
   */
  openOnClick: boolean;
  delayCloseMs?: number;
} & TooltipRootProps) {
  return (
    <TooltipContextProvider openOnClick={openOnClick} delayCloseMs={delayCloseMs}>
      <TooltipComponent {...props}>{children}</TooltipComponent>
    </TooltipContextProvider>
  );
}

const TooltipContent = forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    size?: 'xs' | 'sm';
  } & React.ComponentProps<typeof Content>
>(function TooltipContent({ children, size = 'xs', ...props }, ref) {
  const { sideOffset = 6, side = 'top', avoidCollisions = true, className, ...rest } = props;
  const layoutElement = useGetLayoutContainer();

  return (
    <StyledContent
      sideOffset={sideOffset}
      side={side}
      avoidCollisions={avoidCollisions}
      collisionBoundary={layoutElement}
      className={clsx(
        'z-tooltip bg-neutral900 font-base font-medium text-white shadow-secondary ',
        size === 'xs' ? 'rounded-sm px-2.5 py-2 text-base-xs' : 'rounded-md px-2 py-3 text-base-s',
        className,
      )}
      {...rest}
      ref={ref}
    >
      {children}
    </StyledContent>
  );
});

const StyledContent = styled(Content, {
  ...animationStyles,
});

function TooltipTrigger({
  children,
  ...props
}: { children: React.ReactNode } & React.ComponentProps<typeof Trigger>) {
  const { className } = props;
  const { openContent, setOpenContent, openOnClick, delayCloseMs } = useTooltipContext();

  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const onLeave = useStableCallback(() => {
    if (!!delayCloseMs) {
      // Clear any previous timeout if it exists
      // we're using a ref to store the timeout so that we can access it in the mouseover event
      // handles the case where the user hovers in and out quickly and we don't want to close the tooltip
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      // set new timeout and store it in the ref
      timeoutRef.current = setTimeout(() => {
        setOpenContent(false);
      }, delayCloseMs);

      return;
    }
    setOpenContent(false);
  });

  return (
    <Trigger
      className={clsx('cursor-default hover:cursor-default', className)}
      onClick={() => {
        // we want to open on click only on mobile devices & disable this for
        // dektop devices so that hover interaction
        // contrary to the desktop hover, on click on mobile is smoother
        if (!openOnClick) return;
        setOpenContent(!openContent);
      }}
      onMouseOver={() => {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null; // Reset the timeout ref
        }

        //handling hover interaction for desktop devices using this event handler
        //so that hover interaction is smoother and we can disable on mobile
        if (openOnClick) return;

        setOpenContent(true);
      }}
      onMouseLeave={onLeave}
      onBlur={onLeave}
      {...props}
    >
      {children}
    </Trigger>
  );
}

Tooltip.StyledContent = TooltipContent;
Tooltip.Content = Content;
Tooltip.Trigger = TooltipTrigger;
Tooltip.Portal = Portal;
